import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useProviderConfigActionsController_fetchHistory } from '@/services/hooks';

import { mapFetchHistory } from './actionMappers';
import type { FetchHistoryInput, FetchHistoryResult } from './actionTypes';
import { type ProviderConfigError, parsedErrorOrNull } from './errors';
import { providerConfigQueryKey } from './queryKeys';

export interface UseFetchHistoryResult {
    /** Rejects with the raw error; `error` holds it parsed. */
    run: (input: FetchHistoryInput) => Promise<FetchHistoryResult>;
    isRunning: boolean;
    error: ProviderConfigError | null;
}

export function useFetchHistory(locationId: number): UseFetchHistoryResult {
    const queryClient = useQueryClient();
    const mutation = useProviderConfigActionsController_fetchHistory({
        // The written days change the pen's health (days with data, last fetch).
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: providerConfigQueryKey(locationId),
                refetchType: 'all',
            }),
    });
    const { mutateAsync } = mutation;

    const run = useCallback(
        async ({ cageId, from, to }: FetchHistoryInput) => {
            const response = await mutateAsync({
                locationId,
                cageId,
                requestBody: { from, to },
            });
            return mapFetchHistory(response.data);
        },
        [mutateAsync, locationId]
    );
    const error = useMemo(() => parsedErrorOrNull(mutation.error), [mutation.error]);

    return { run, isRunning: mutation.isPending, error };
}
